/**
 * Lead Status Enum
 * Defines all possible lead status values used throughout the application
 */
export const LeadStatus = {
  NEW_LEADS: 'New Leads',
  FOLLOW_UP: 'Follow Up',
  CONTACTED: 'Contacted',
  QUALIFIED: 'Qualified',
  CUSTOMER: 'Customer',
  LOST: 'Lost'
};

/**
 * Lead Status Colors
 * Maps each status to its corresponding color for UI display
 */
export const LeadStatusColors = {
  [LeadStatus.NEW_LEADS]: '#f5f6fa',
  [LeadStatus.FOLLOW_UP]: '#ffe5b4',
  [LeadStatus.CONTACTED]: '#b4d8ff',
  [LeadStatus.QUALIFIED]: '#e6e6fa',
  [LeadStatus.CUSTOMER]: '#b4ffb4',
  [LeadStatus.LOST]: '#ffd6d6'
};

/**
 * Lead Status Order
 * Defines the order in which statuses should be displayed
 */
export const LeadStatusOrder = [
  LeadStatus.NEW_LEADS,
  LeadStatus.FOLLOW_UP,
  LeadStatus.CONTACTED,
  LeadStatus.QUALIFIED,
  LeadStatus.CUSTOMER,
  LeadStatus.LOST
];

/**
 * Bootstrap Badge Colors for Lead Status
 * Maps each status to its corresponding Bootstrap badge variant
 */
export const LeadStatusBadgeColors = {
  [LeadStatus.NEW_LEADS]: 'warning',
  [LeadStatus.FOLLOW_UP]: 'info',
  [LeadStatus.CONTACTED]: 'success',
  [LeadStatus.QUALIFIED]: 'primary',
  [LeadStatus.CUSTOMER]: 'success',
  [LeadStatus.LOST]: 'danger'
};

/**
 * Helper function to check if a status is valid
 * @param {string} status - The status to validate
 * @returns {boolean} - True if the status is valid
 */
export const isValidLeadStatus = (status) => {
  return Object.values(LeadStatus).includes(status);
};

/**
 * Helper function to get the next valid statuses for a given status
 * @param {string} currentStatus - The current status
 * @returns {string[]} - Array of valid next statuses
 */
export const getNextValidStatuses = (currentStatus) => {
  const statusIndex = LeadStatusOrder.indexOf(currentStatus);
  if (statusIndex === -1) return [];
  
  // Allow moving to adjacent statuses or skipping one step
  const validNext = [];
  for (let i = Math.max(0, statusIndex - 1); i <= Math.min(LeadStatusOrder.length - 1, statusIndex + 2); i++) {
    if (i !== statusIndex) {
      validNext.push(LeadStatusOrder[i]);
    }
  }
  
  // Special rules
  if (currentStatus === LeadStatus.NEW_LEADS) {
    // Can't go directly to Customer from New Leads
    return validNext.filter(status => status !== LeadStatus.CUSTOMER);
  }
  
  if (currentStatus !== LeadStatus.QUALIFIED) {
    // Only Qualified leads can become Customers
    return validNext.filter(status => status !== LeadStatus.CUSTOMER);
  }
  
  return validNext;
};

/** Short label for pickers and compact UI (e.g. group builder). */
export const leadStatusShortLabel = (status) => {
  if (status === LeadStatus.NEW_LEADS) return 'New';
  if (status === LeadStatus.FOLLOW_UP) return 'Followup';
  if (status === LeadStatus.CONTACTED) return 'Contacted';
  if (status === LeadStatus.QUALIFIED) return 'Qualified';
  if (status === LeadStatus.CUSTOMER) return 'Customer';
  if (status === LeadStatus.LOST) return 'Lost';
  return status || '—';
};
